import React, { useState, useEffect, useCallback } from "react";
import {
  listPaymentModes,
  listPaymentModesPortal,
  createPaymentMode,
  createPaymentModePortal,
  updatePaymentMode,
  updatePaymentModePortal,
  deletePaymentMode,
  deletePaymentModePortal
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Checkbox,
  Table,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  Plus,
  CreditCard
} from "lucide-react";
import {
  useToast
} from "../../context/ToastContext";

const emptyForm = {
  code: "",
  displayName: "",
  requiresTransactionId: false,
  sortOrder: 0,
};

const PaymentModesPage = ({ isPortal = false }) => {
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await listPaymentModesPortal() : await listPaymentModes();
      setModes(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load payment modes.");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addMode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isPortal) await createPaymentModePortal(form);
      else await createPaymentMode(form);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to create payment mode.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      if (isPortal) await updatePaymentModePortal(m._id, { isActive: !m.isActive });
      else await updatePaymentMode(m._id, { isActive: !m.isActive });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to update payment mode.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Are you sure you want to remove this payment mode?")) return;
    try {
      if (isPortal) await deletePaymentModePortal(id);
      else await deletePaymentMode(id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to remove payment mode.");
    }
  };

  const columns = [
    {
      key: "code",
      header: "Code",
      render: (row) => <span className="font-mono text-sm">{row.code}</span>,
    },
    { key: "displayName", header: "Display name" },
    {
      key: "requiresTransactionId",
      header: "Transaction ID",
      render: (row) => (row.requiresTransactionId ? "Required" : "Optional"),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Badge variant={row.isActive ? "success" : "default"}>
          {row.isActive ? "Active" : "Disabled"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => toggleActive(row)}>
            {row.isActive ? "Disable" : "Enable"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-danger-500 hover:text-danger-700"
            onClick={() => remove(row._id)}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Payment modes</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Enable or disable collection methods available at your institution.
        </p>
      </div>

      

      <Card>
        <CardHeader>
          <CardTitle>Add payment mode</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={addMode} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 items-end">
            <Input
              label="Code"
              placeholder="e.g. UPI"
              required
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
            />
            <Input
              label="Display name"
              placeholder="e.g. UPI / QR"
              required
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
            <div className="pb-1">
              <Checkbox
                label="Requires transaction ID"
                checked={form.requiresTransactionId}
                onChange={(checked) =>
                  setForm({ ...form, requiresTransactionId: checked })
                }
              />
            </div>
            <Button type="submit" isLoading={submitting} leftIcon={<Plus className="h-4 w-4" />}>
              Add mode
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
          <div className="flex gap-4">
            <Skeleton height={12} width="20%" />
            <Skeleton height={12} width="25%" />
            <Skeleton height={12} width="15%" />
            <Skeleton height={12} width="20%" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={16} width="20%" />
              <Skeleton height={16} width="25%" />
              <Skeleton height={16} width="15%" />
              <Skeleton height={16} width="20%" />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          data={modes}
          emptyState={
            <EmptyState
              icon={<CreditCard />}
              title="No payment modes"
              description="Add cash, UPI, card, or other modes for fee collection."
            />
          }
        />
      )}
    </div>
  );
};

export default PaymentModesPage;
